import {Component, CUSTOM_ELEMENTS_SCHEMA, HostListener, OnDestroy, OnInit} from '@angular/core';

import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {
  HubDonationFormAmount,
  HubDonationFormDesignation,
  HubDonationFormEditorResponse,
  HubDonationFormValidationMessage,
  HubForm
} from '../../model/hub-session';
import {HubApiService} from '../../services/hub-api.service';
import {UnsavedChangesComponent} from '../../guards/unsaved-changes.guard';

type FormEditorSection = 'properties' | 'style' | 'amounts' | 'designations' | 'translations' | 'embed' | 'twint';
type SaveState = 'saved' | 'saving' | 'dirty';

@Component({
  selector: 'hub-forms',
  imports: [FormsModule, RouterLink],
  templateUrl: './forms.component.html',
  styleUrl: './forms.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class FormsComponent implements OnInit, OnDestroy, UnsavedChangesComponent {
  forms: HubForm[] = [];
  editor: HubDonationFormEditorResponse | null = null;
  newFormName = '';
  newOnceAmount = 50;
  newMonthlyAmount = 20;
  newDesignationName = '';
  createModalOpen = false;
  loading = true;
  error = '';
  success = '';
  formId = 0;
  activeSection: FormEditorSection = 'properties';
  activeFrequency: 'once' | 'monthly' = 'once';
  activeLanguage = '';
  previewLanguage = '';
  saveState: SaveState = 'saved';
  readonly sectionItems: Array<{ value: FormEditorSection; labelKey: string }> = [
    {value: 'properties', labelKey: 'FormProperties'},
    {value: 'style', labelKey: 'FormStyle'},
    {value: 'amounts', labelKey: 'FormAmounts'},
    {value: 'designations', labelKey: 'FormDesignations'},
    {value: 'translations', labelKey: 'FormTranslations'},
    {value: 'embed', labelKey: 'InsertIntoWebpage'},
    {value: 'twint', labelKey: 'twint'}
  ];
  readonly frequencyItems: Array<{ value: 'once' | 'monthly'; labelKey: string }> = [
    {value: 'once', labelKey: 'FrequencyOnceTabTitle'},
    {value: 'monthly', labelKey: 'FrequencyMonthlyTabTitle'}
  ];
  private autosaveTimer: ReturnType<typeof setTimeout> | null = null;
  private autosaveInFlight = false;
  private autosaveQueued = false;
  private readonly autosaveDelayMs = 500;
  private readonly fieldAutosaveDelayMs = 1200;

  constructor(
    private hubApi: HubApiService,
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer
  ) {
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.formId = Number(params.get('id') || 0);
      this.resetMessages();
      this.loading = true;
      if (this.formId > 0) {
        this.loadEditor();
      } else {
        this.loadList();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.autosaveTimer) clearTimeout(this.autosaveTimer);
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: BeforeUnloadEvent): void {
    if (!this.hasUnsavedChanges()) return;
    event.preventDefault();
    event.returnValue = this.unsavedChangesMessage();
  }

  get isEditor(): boolean {
    return this.formId > 0;
  }

  createForm(): void {
    const name = this.newFormName.trim();
    if (!name) return;
    this.resetMessages();
    const existingIds = new Set(this.forms.map((form) => form.id));
    this.hubApi.createForm(name).subscribe({
      next: (response) => {
        const created = response.forms.find((form) => !existingIds.has(form.id)) || response.forms[response.forms.length - 1];
        this.forms = response.forms;
        this.newFormName = '';
        this.createModalOpen = false;
        if (created) {
          this.router.navigate(['/forms', created.id]);
        } else {
          this.success = this.t('HubFormCreatedMessage');
        }
      },
      error: (error) => this.error = error.error?.message || this.t('HubFormCreateFailedMessage')
    });
  }

  deleteForm(form: HubForm): void {
    if (!confirm(this.t('FormDeletionConfirmation'))) return;
    this.resetMessages();
    this.hubApi.deleteForm(form.id).subscribe({
      next: (response) => {
        this.forms = response.forms;
        this.success = this.t('HubFormDeletedMessage');
      },
      error: (error) => this.error = error.error?.message || this.t('HubFormDeleteFailedMessage')
    });
  }

  markDirty(delay = this.autosaveDelayMs): void {
    if (!this.editor) return;
    this.saveState = 'dirty';
    if (this.autosaveTimer) clearTimeout(this.autosaveTimer);
    this.autosaveTimer = setTimeout(() => this.saveNow(), delay);
  }

  saveTextField(): void {
    this.markDirty(this.fieldAutosaveDelayMs);
  }

  addAmount(forFrequency: 'once' | 'monthly'): void {
    if (!this.editor) return;
    const amount = forFrequency === 'monthly' ? this.newMonthlyAmount : this.newOnceAmount;
    this.runAfterCurrentSave(() => {
      if (!this.editor) return;
      this.saveState = 'saving';
      this.hubApi.addDonationFormAmount(this.editor.form.id, forFrequency, amount).subscribe({
        next: (data) => {
          this.applyEditor(data);
          if (forFrequency === 'monthly') {
            this.newMonthlyAmount = 20;
          } else {
            this.newOnceAmount = 50;
          }
        },
        error: (error) => this.handleSaveError(error)
      });
    });
  }

  deleteAmount(amount: HubDonationFormAmount): void {
    if (!this.editor) return;
    this.runAfterCurrentSave(() => {
      if (!this.editor) return;
      this.saveState = 'saving';
      this.hubApi.deleteDonationFormAmount(this.editor.form.id, amount.id).subscribe({
        next: (data) => this.applyEditor(data),
        error: (error) => this.handleSaveError(error)
      });
    });
  }

  addDesignation(): void {
    if (!this.editor) return;
    const designation = this.newDesignationName.trim();
    if (!designation) return;
    this.runAfterCurrentSave(() => {
      if (!this.editor) return;
      this.saveState = 'saving';
      this.hubApi.addDonationFormDesignation(this.editor.form.id, designation).subscribe({
        next: (data) => {
          this.applyEditor(data);
          this.newDesignationName = '';
        },
        error: (error) => this.handleSaveError(error)
      });
    });
  }

  deleteDesignation(designation: HubDonationFormDesignation): void {
    if (!this.editor) return;
    this.runAfterCurrentSave(() => {
      if (!this.editor) return;
      this.saveState = 'saving';
      this.hubApi.deleteDonationFormDesignation(this.editor.form.id, designation.id).subscribe({
        next: (data) => this.applyEditor(data),
        error: (error) => this.handleSaveError(error)
      });
    });
  }

  uploadDesignationPicture(designation: HubDonationFormDesignation, event: Event): void {
    if (!this.editor) return;
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.set(`designationPicture-${designation.id}`, file);
    this.runAfterCurrentSave(() => {
      if (!this.editor) return;
      this.saveState = 'saving';
      this.hubApi.saveDonationForm(this.editor.form.id, formData).subscribe({
        next: (data) => {
          input.value = '';
          this.applyEditor(data);
        },
        error: (error) => {
          input.value = '';
          this.handleSaveError(error);
        }
      });
    });
  }

  validationFor(path: string): string {
    const message = this.validationItem(path);
    return message?.message || '';
  }

  sectionInvalid(section: FormEditorSection | 'once' | 'monthly' | string): boolean {
    if (!this.editor) return false;
    return this.editor.validation.some((message) => message.path.includes(section));
  }

  validationCount(section?: string): number {
    if (!this.editor) return 0;
    if (!section) return this.editor.validation.length;
    return this.editor.validation.filter((message) => message.path.includes(section)).length;
  }

  amounts(forFrequency: 'once' | 'monthly'): HubDonationFormAmount[] {
    return this.editor?.amounts.filter((amount) => amount.forFrequency === forFrequency) || [];
  }

  previewUrl(language: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl((this.editor?.previewBaseUrl || '') + encodeURIComponent(language));
  }

  copyText(value: string): void {
    this.resetMessages();
    if (!navigator.clipboard) {
      this.error = this.t('HubClipboardUnavailableMessage');
      return;
    }
    navigator.clipboard.writeText(value).then(() => {
      this.success = this.t('HubEmbedCodeCopiedMessage');
    }).catch(() => {
      this.error = this.t('HubEmbedCodeCopyFailedMessage');
    });
  }

  saveStatusLabel(): string {
    if (this.saveState === 'saving') return this.t('HubAutosaving');
    if (this.saveState === 'dirty') return this.t('HubAutosaveUnsaved');
    return this.t('HubAutosaved');
  }

  hasUnsavedChanges(): boolean {
    return this.saveState !== 'saved' || !!this.autosaveTimer || this.autosaveInFlight || this.autosaveQueued;
  }

  unsavedChangesMessage(): string {
    return this.t('HubUnsavedChangesWarning');
  }

  t(key: string): string {
    return this.hubApi.translate(key) || key;
  }

  private loadList(): void {
    this.editor = null;
    this.hubApi.listForms().subscribe({
      next: (response) => {
        this.forms = response.forms;
        this.loading = false;
      },
      error: (error) => {
        this.error = error.error?.message || this.t('HubFormsLoadFailedMessage');
        this.loading = false;
      }
    });
  }

  private loadEditor(): void {
    this.hubApi.loadDonationForm(this.formId).subscribe({
      next: (data) => {
        this.applyEditor(data);
        this.restoreEditorPreferences();
        this.loading = false;
      },
      error: (error) => {
        this.error = error.error?.message || this.t('HubFormEditorLoadFailedMessage');
        this.loading = false;
      }
    });
  }

  private saveNow(): void {
    if (!this.editor) return;
    if (this.autosaveTimer) {
      clearTimeout(this.autosaveTimer);
      this.autosaveTimer = null;
    }
    if (this.autosaveInFlight) {
      this.autosaveQueued = true;
      return;
    }
    this.autosaveInFlight = true;
    this.saveState = 'saving';
    this.hubApi.saveDonationForm(this.editor.form.id, {
      form: this.editor.form,
      amounts: this.editor.amounts,
      designations: this.editor.designations,
      translations: this.editor.translations
    }).subscribe({
      next: (data) => {
        this.autosaveInFlight = false;
        if (this.autosaveQueued) {
          this.autosaveQueued = false;
          this.markDirty();
          return;
        }
        this.applyEditor(data);
      },
      error: (error) => {
        this.autosaveInFlight = false;
        this.autosaveQueued = false;
        this.handleSaveError(error);
      }
    });
  }

  private applyEditor(data: HubDonationFormEditorResponse): void {
    this.editor = data;
    this.activeLanguage = this.activeLanguage && data.languages.includes(this.activeLanguage) ? this.activeLanguage : (data.languages[0] || 'en');
    this.previewLanguage = this.previewLanguage && data.languages.includes(this.previewLanguage) ? this.previewLanguage : this.activeLanguage;
    this.saveState = 'saved';
    this.error = '';
  }

  private restoreEditorPreferences(): void {
    const rawSection = this.readCookie('activeFormEditorSection').replace('Settings', '');
    const section = (rawSection === 'insertIntoWebpage' ? 'embed' : rawSection) as FormEditorSection;
    if (['properties', 'style', 'amounts', 'designations', 'translations', 'embed', 'twint'].includes(section)) {
      this.activeSection = section;
    }
    const frequency = this.readCookie('activeFrequencyTab');
    if (frequency === 'once' || frequency === 'monthly') {
      this.activeFrequency = frequency;
    }
    const language = this.readCookie('activeLanguageTab');
    if (language && this.editor?.languages.includes(language)) {
      this.activeLanguage = language;
    }
    const previewLanguage = this.readCookie('previewLanguage');
    if (previewLanguage && this.editor?.languages.includes(previewLanguage)) {
      this.previewLanguage = previewLanguage;
    }
  }

  setSection(section: FormEditorSection): void {
    this.activeSection = section;
    this.writeCookie('activeFormEditorSection', `${section}Settings`);
  }

  setFrequency(frequency: 'once' | 'monthly'): void {
    this.activeFrequency = frequency;
    this.writeCookie('activeFrequencyTab', frequency);
  }

  setLanguage(language: string): void {
    this.activeLanguage = language;
    this.writeCookie('activeLanguageTab', language);
  }

  setPreviewLanguage(language: string): void {
    this.previewLanguage = language;
    this.activeLanguage = language;
    this.writeCookie('previewLanguage', language);
    this.writeCookie('activeLanguageTab', language);
  }

  private validationItem(path: string): HubDonationFormValidationMessage | undefined {
    const parts = path.split('.');
    return this.editor?.validation.find((message) => message.path.join('.') === path || parts.every((part) => message.path.includes(part)));
  }

  private runAfterCurrentSave(action: () => void): void {
    if (!this.editor) return;
    if (this.autosaveInFlight) {
      setTimeout(() => this.runAfterCurrentSave(action), 100);
      return;
    }
    if (this.autosaveTimer) {
      clearTimeout(this.autosaveTimer);
      this.autosaveTimer = null;
    }
    if (this.saveState !== 'dirty') {
      action();
      return;
    }

    this.autosaveInFlight = true;
    this.saveState = 'saving';
    this.hubApi.saveDonationForm(this.editor.form.id, {
      form: this.editor.form,
      amounts: this.editor.amounts,
      designations: this.editor.designations,
      translations: this.editor.translations
    }).subscribe({
      next: (data) => {
        this.autosaveInFlight = false;
        if (this.autosaveQueued) {
          this.autosaveQueued = false;
          this.applyEditor(data);
          this.markDirty();
          setTimeout(() => this.runAfterCurrentSave(action), this.autosaveDelayMs + 20);
          return;
        }
        this.applyEditor(data);
        action();
      },
      error: (error) => {
        this.autosaveInFlight = false;
        this.handleSaveError(error);
      }
    });
  }

  private handleSaveError(error: { error?: { message?: string } }): void {
    this.saveState = 'dirty';
    this.error = error.error?.message || this.t('HubFormEditorSaveFailedMessage');
  }

  private resetMessages(): void {
    this.error = '';
    this.success = '';
  }

  private readCookie(name: string): string {
    const cookie = document.cookie.split('; ').find((item) => item.startsWith(`${name}=`));
    return cookie ? decodeURIComponent(cookie.split('=').slice(1).join('=')) : '';
  }

  private writeCookie(name: string, value: string): void {
    document.cookie = `${name}=${encodeURIComponent(value)};path=/;SameSite=Lax;Secure`;
  }
}
